// src/pwa/install.js
/**
 * "Install the app" support for the phone apps (/field, /portal).
 * Chrome / Edge / Samsung fire `beforeinstallprompt` once, often before React mounts, so it is
 * captured here at import time (imported from src/index.jsx) and handed to the banner later.
 * iOS has no install event: the banner shows Share → Add to Home Screen instructions instead.
 */
const DISMISS_KEY = 'sos-install-dismissed';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

let deferred = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep Chrome's own mini-infobar out of the way; our banner offers it
    deferred = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

// iPadOS reports itself as a Mac; touch points give it away.
export const isIos = () =>
  typeof navigator !== 'undefined' &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/** 'prompt' (native dialog available), 'ios' (show instructions) or null (nothing to offer). */
export const installMode = () => {
  if (isStandalone()) return null;
  if (deferred) return 'prompt';
  if (isIos()) return 'ios';
  return null;
};

export async function promptInstall() {
  if (!deferred) return 'unavailable';
  const e = deferred;
  deferred = null; // the event can only be used once
  e.prompt();
  const { outcome } = await e.userChoice;
  notify();
  return outcome; // 'accepted' | 'dismissed'
}

export const isDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return !!at && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

export const dismiss = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode: banner just comes back next visit */
  }
  notify();
};
